import { BaseLLMAdapter } from './BaseLLMAdapter';
import { ModelConfig } from '../../config';
import { SceneData, LLMResponse } from '../../types';
import { ResponseProcessor } from '../../processors/response';
import { ReasoningVisualizer } from '../../components/ReasoningVisualizer';
import { LLMDebugger } from '../../utils/LLMDebugger';

/**
 * OpenRouter API适配器
 */
export class OpenRouterAdapter extends BaseLLMAdapter {
  private responseProcessor: ResponseProcessor;
  private reasoningVisualizer: ReasoningVisualizer;
  private debugger: LLMDebugger;
  
  /**
   * 构造函数
   * @param config 模型配置
   * @param debugMode 是否处于调试模式
   */
  constructor(config: ModelConfig, debugMode: boolean = false) {
    super(config, debugMode);
    this.responseProcessor = new ResponseProcessor(debugMode);
    this.reasoningVisualizer = ReasoningVisualizer.getInstance();
    this.debugger = LLMDebugger.getInstance();
    this.debugger.setDebugMode(debugMode);
    
    // 检查全局调试标志
    if ((window as any).__OPENROUTER_DEBUG__) {
      this.debugMode = true;
      this.debugger.setDebugMode(true);
    }
    
    // 显示模型提示信息
    if (this.config.id === 'google/gemini-2.0-pro-exp-02-05:free') {
      this.showModelTierWarning();
    }
  }
  
  /**
   * 显示模型层级警告
   */
  private showModelTierWarning(): void {
    // 创建警告元素
    const warningEl = document.createElement('div');
    warningEl.style.position = 'fixed';
    warningEl.style.bottom = '50px';
    warningEl.style.left = '50%';
    warningEl.style.transform = 'translateX(-50%)';
    warningEl.style.backgroundColor = 'rgba(255, 193, 7, 0.9)';
    warningEl.style.color = 'black';
    warningEl.style.padding = '10px 20px';
    warningEl.style.borderRadius = '5px';
    warningEl.style.zIndex = '10000';
    warningEl.style.maxWidth = '80%';
    warningEl.style.textAlign = 'center';
    warningEl.textContent = '注意: Gemini Pro 模型可能需要更高级别的 OpenRouter API Key。如果遇到"Provider error"，请尝试 Flash 模型或升级您的 API Key。';
    
    // 添加到页面
    document.body.appendChild(warningEl);
    
    // 5秒后自动移除
    setTimeout(() => {
      if (warningEl.parentNode) {
        warningEl.parentNode.removeChild(warningEl);
      }
    }, 5000);
  }
  
  /**
   * 准备OpenRouter请求
   */
  async prepareRequest(prompt: string): Promise<{
    endpoint: string;
    headers: Headers;
    body: any;
  }> {
    const endpoint = this.getEndpoint();
    const headers = this.createBaseHeaders();
    
    // 基础请求体
    const baseBody = {
      ...this.createBaseBody(prompt),
      model: this.config.id,
      stream: true,
      messages: [
        {
          role: "system",
          content: "你是一个赛博朋克2077风格游戏世界的创造者和叙事者。这是一个充满高科技和低生活的世界，霓虹与阴影并存，义体与人性交织。"
        },
        {
          role: "user",
          content: prompt
        }
      ]
    };
    
    // 为GPT-4o-mini添加结构化输出格式
    let body = baseBody;
    if (this.config.id === 'openai/gpt-4o-mini') {
      body = {
        ...baseBody,
        response_format: {
          type: "json_schema",
          json_schema: {
            name: "scene_data",
            strict: true,
            schema: {
              type: "object",
              properties: {
                scene_text: {
                  type: "string",
                  description: "场景的描述文本"
                },
                npc_dialogue: {
                  type: "string",
                  description: "NPC的对话内容"
                },
                choices: {
                  type: "array",
                  description: "玩家可选择的选项数组",
                  items: {
                    type: "object",
                    properties: {
                      text: {
                        type: "string",
                        description: "选项的文本描述"
                      },
                      next_scene: {
                        type: "string",
                        description: "选择此选项后的下一个场景ID或标识符"
                      },
                      requirements: {
                        type: ["string", "null"],
                        description: "选择此选项的要求，如果没有要求则为null"
                      }
                    },
                    required: ["text", "next_scene", "requirements"]
                  }
                }
              },
              required: ["scene_text", "choices"]
            }
          }
        }
      };
    }
    
    // 显示推理可视化
    this.reasoningVisualizer.show(this.config.id, 'openrouter');
    
    // 记录请求详情
    this.debugger.logModelCall(this.config.id, {
      endpoint,
      model: this.config.id,
      headers: Array.from(headers.entries()),
      body
    });
    
    // 增强调试: 如果开启了OpenRouter调试
    if ((window as any).__OPENROUTER_DEBUG__) {
      let debugInfo = '';
      try {
        // 获取 API key 的前几个字符和后几个字符，中间用 * 替代
        const apiKey = this.getApiKey();
        const maskedKey = apiKey.length > 10 ? 
          `${apiKey.substring(0, 5)}...${apiKey.substring(apiKey.length - 5)}` : 
          '未设置';
          
        // 标记 API key 是否是 Free 还是 Paid 层级
        const keyTier = this.getApiKeyTier(apiKey);
        
        // 构建调试信息
        debugInfo = `
=== OpenRouter 请求调试信息 ===
端点: ${endpoint}
模型: ${this.config.id}
API Key: ${maskedKey}
API Key 层级: ${keyTier}
请求时间: ${new Date().toLocaleString()}

请求头:
${Array.from(headers.entries()).map(([key, value]) => {
  // 处理 Authorization 头，不显示完整的 API key
  if (key === 'Authorization') {
    return `${key}: Bearer sk-...`;
  }
  return `${key}: ${value}`;
}).join('\n')}

请求体:
${JSON.stringify(body, null, 2)}
`;
        
        // 显示调试信息
        this.showDebugPanel(debugInfo);
      } catch (error) {
        console.error('生成调试信息时出错:', error);
      }
    }
    
    return { endpoint, headers, body };
  }
  
  /**
   * 判断 API key 的层级
   */
  private getApiKeyTier(apiKey: string): string {
    if (!apiKey) return '未设置';
    
    // OpenRouter free tier API keys 通常以 sk-or-v1- 开头
    if (apiKey.startsWith('sk-or-v1-')) {
      return 'Free 层级 (sk-or-v1)';
    }
    
    // Paid tier API keys 可能有不同前缀
    if (apiKey.startsWith('sk-or-')) {
      return 'Paid 层级 (sk-or)';
    }
    
    return '未知层级';
  }
  
  /**
   * 处理响应
   */
  async processResponse(response: Response): Promise<SceneData | null> {
    // 增强调试: 记录响应状态
    if ((window as any).__OPENROUTER_DEBUG__) {
      const debugPanel = document.querySelector('.debug-panel');
      if (debugPanel) {
        const existingContent = debugPanel.textContent || '';
        debugPanel.textContent = existingContent + `\n\n=== 响应状态 ===\n状态码: ${response.status}\n状态文本: ${response.statusText}`;
      }
    }
    
    try {
      return await this.handleStreamResponse(response);
    } catch (error) {
      // 记录错误并获取分析
      const errorAnalysis = await this.debugger.analyzeError(error as Error, this.config.id, {
        responseStatus: response.status,
        responseHeaders: Array.from(response.headers.entries())
      });
      
      // 隐藏推理可视化
      this.reasoningVisualizer.hide();
      
      // 增强调试: 记录错误
      if ((window as any).__OPENROUTER_DEBUG__) {
        this.appendDebugInfo(`\n\n=== 错误信息 ===\n${(error as Error).message}\n\n${errorAnalysis}`);
      }
      
      // 判断是否是 Provider error，这通常意味着 API key 权限不足
      const errorMessage = (error as Error).message;
      if (errorMessage.includes('Provider') && this.config.id.includes('pro')) {
        this.showProviderErrorMessage();
      }
      
      // 抛出带有分析的错误
      throw new Error(`OpenRouter处理响应失败: ${(error as Error).message}\n\n${errorAnalysis}`);
    }
  }

  /**
   * 显示 Provider 错误信息
   */
  private showProviderErrorMessage(): void {
    // 先检查是否已经存在错误提示框，避免重复显示
    const existingError = document.querySelector('.openrouter-error-modal');
    if (existingError) {
      return; // 已经显示了错误提示框，不再重复显示
    }
    
    const errorEl = document.createElement('div');
    errorEl.className = 'openrouter-error-modal'; // 添加类名以便识别
    errorEl.style.position = 'fixed';
    errorEl.style.top = '50%';
    errorEl.style.left = '50%';
    errorEl.style.transform = 'translate(-50%, -50%)';
    errorEl.style.backgroundColor = 'rgba(220, 53, 69, 0.9)';
    errorEl.style.color = 'white';
    errorEl.style.padding = '20px';
    errorEl.style.borderRadius = '5px';
    errorEl.style.zIndex = '10000';
    errorEl.style.maxWidth = '80%';
    errorEl.style.textAlign = 'center';
    errorEl.innerHTML = `
      <h3 style="margin-top: 0;">OpenRouter API 访问错误</h3>
      <p>您的 API Key 可能没有 Gemini Pro 模型的访问权限。</p>
      <p>解决方案:</p>
      <ul style="text-align: left;">
        <li>尝试使用 Gemini Flash 模型（免费用户可用）</li>
        <li>升级您的 OpenRouter 账户到付费层级</li>
        <li>确认您的 API Key 没有打字错误</li>
      </ul>
      <button class="close-provider-error" style="background: white; color: #dc3545; border: none; padding: 5px 10px; border-radius: 3px; cursor: pointer; margin-top: 10px;">关闭</button>
    `;
    
    document.body.appendChild(errorEl);
    
    // 直接给按钮添加点击事件
    const closeButton = errorEl.querySelector('.close-provider-error');
    if (closeButton) {
      closeButton.addEventListener('click', function() {
        errorEl.remove(); // 直接移除元素
      });
    }
    
    // 添加一个点击背景关闭的功能
    errorEl.addEventListener('click', function(e) {
      if (e.target === errorEl) { // 只有点击背景时才关闭
        errorEl.remove();
      }
    });
    
    // 添加一个 Escape 键关闭功能
    function handleEscapeKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        errorEl.remove();
        document.removeEventListener('keydown', handleEscapeKey);
      }
    }
    document.addEventListener('keydown', handleEscapeKey);
  }

  private async handleStreamResponse(response: Response): Promise<SceneData | null> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    let accumulatedContent = '';
    let reasoningContent = '';
    let errorDetected = false;
    
    // 增强调试: 记录响应内容
    if ((window as any).__OPENROUTER_DEBUG__) {
      this.appendDebugInfo(`\n\n=== 响应内容 ===\n`);
    }
    
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        
        // 记录流式响应片段
        this.debugger.logStreamChunk(this.config.id, chunk);

        // 增强调试: 记录接收到的片段
        if ((window as any).__OPENROUTER_DEBUG__) {
          this.appendDebugInfo(`${chunk}\n`);
        }
        
        // 检测错误
        if (chunk.includes('"error"') && !errorDetected) {
          errorDetected = true;
          try {
            // 尝试提取错误信息
            const errorMatch = chunk.match(/data: (\{"error":.+\})/);
            if (errorMatch && errorMatch[1]) {
              const errorData = JSON.parse(errorMatch[1]);
              if (errorData.error && errorData.error.message) {
                throw new Error(`OpenRouter错误: ${errorData.error.message}`);
              }
            }
          } catch (e) {
            if (e instanceof Error && e.message.startsWith('OpenRouter错误:')) {
              throw e;
            }
          }
        }

        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ') && !line.includes('[DONE]')) {
            try {
              const jsonStr = line.substring(6);
              const jsonData = JSON.parse(jsonStr);

              if (jsonData.choices?.[0]?.delta?.content) {
                const content = jsonData.choices[0].delta.content;
                accumulatedContent += content;
                reasoningContent += content;
                
                // 更新推理可视化
                this.reasoningVisualizer.update(reasoningContent);
              }
            } catch (error) {
              // 忽略JSON解析错误，可能是错误消息
              if (!(error instanceof SyntaxError)) {
                this.debugger.logModelResponse(this.config.id, null, error as Error);
              }
            }
          }
        }
      }

      // 处理完成，隐藏推理可视化
      setTimeout(() => {
        this.reasoningVisualizer.hide();
      }, 1000);

      // 如果没有有效内容返回，可能是出错了
      if (!accumulatedContent.trim()) {
        throw new Error('OpenRouter 没有返回有效内容');
      }

      // 构造响应对象
      const mockResponse: LLMResponse = {
        choices: [
          {
            message: {
              content: accumulatedContent
            }
          }
        ]
      };

      // 处理响应
      const result = await this.responseProcessor.processResponse(mockResponse);
      this.debugger.logModelResponse(this.config.id, result);
      
      // 增强调试: 记录处理后的结果
      if ((window as any).__OPENROUTER_DEBUG__) {
        this.appendDebugInfo(`\n\n=== 处理后的结果 ===\n${JSON.stringify(result, null, 2)}`);
      }
      
      return result;

    } catch (error) {
      this.reasoningVisualizer.hide();
      this.debugger.logModelResponse(this.config.id, null, error as Error);
      throw error;
    }
  }
  
  /**
   * 显示调试面板
   */
  private showDebugPanel(text: string): void {
    // 检查是否已存在调试面板
    let debugPanel = document.querySelector('.debug-panel');
    if (!debugPanel) {
      debugPanel = document.createElement('div');
      debugPanel.className = 'debug-panel';
      document.body.appendChild(debugPanel);
    }
    
    // 更新调试信息
    debugPanel.textContent = text;
    debugPanel.classList.add('visible');
  }
  
  /**
   * 追加调试信息
   */
  private appendDebugInfo(text: string): void {
    const debugPanel = document.querySelector('.debug-panel');
    if (debugPanel) {
      debugPanel.textContent += text;
    }
  }
} 